import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

export const supportedLanguages = ['ru', 'en'] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

export function isSupportedLanguage(language: string): language is SupportedLanguage {
  return supportedLanguages.includes(language as SupportedLanguage);
}

function normalizeLanguage(language?: string): SupportedLanguage {
  return language?.toLowerCase().startsWith('en') ? 'en' : 'ru';
}

// Определяем язык: сохранённый в localStorage либо 'ru'
const initialLang = (() => {
  try {
    const storedLanguage = typeof window !== 'undefined' ? localStorage.getItem('language') : null;
    return normalizeLanguage(storedLanguage ?? undefined);
  } catch {
    return 'ru' as const;
  }
})();

i18n.use(initReactI18next).init({
  // Ресурсы подгружаем динамически — стартовый бандл меньше
  resources: {},
  lng: initialLang,
  fallbackLng: 'en',
  defaultNS: 'translation',
  ns: ['translation'], // Базовый namespace
  interpolation: {
    escapeValue: false,
  },
  // ✅ КРИТИЧНО: Настраиваем react-i18next для ре-рендера после addResourceBundle
  react: {
    bindI18n: 'languageChanged loaded', // Ре-рендер при изменении языка и загрузке
    bindI18nStore: 'added removed', // Ре-рендер при добавлении/удалении ресурсов
    useSuspense: false, // Не используем Suspense, так как загружаем асинхронно
  },
});

function setDocumentLanguage(language: SupportedLanguage) {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = language;
  }
}

setDocumentLanguage(initialLang);

// Debug logging removed to prevent ERR_INSUFFICIENT_RESOURCES
const logDebug = (_message: string, _data: Record<string, unknown>) => {
  // No-op function
};

// Динамическая загрузка бандла перевода для активного языка
async function loadInitialLanguage(lang: SupportedLanguage) {
  logDebug('loadInitialLanguage START', { lang, hasBundle: i18n.hasResourceBundle(lang, 'translation'), isInitialized: i18n.isInitialized });
  try {
    const mod =
      lang === 'ru'
        ? await import('./locales/ru.json')
        : await import('./locales/en.json');
    const bundle = mod.default;
    logDebug('loadInitialLanguage bundle loaded', { lang, bundleKeys: Object.keys(bundle).slice(0, 10), bundleSize: Object.keys(bundle).length });
    if (!i18n.hasResourceBundle(lang, 'translation')) {
      i18n.addResourceBundle(lang, 'translation', bundle, true, true);
      logDebug('loadInitialLanguage bundle added', { lang, hasBundle: i18n.hasResourceBundle(lang, 'translation'), isInitialized: i18n.isInitialized });
    } else {
      i18n.addResources(lang, 'translation', bundle);
      logDebug('loadInitialLanguage resources added', { lang, hasBundle: i18n.hasResourceBundle(lang, 'translation') });
    }
    logDebug('loadInitialLanguage SUCCESS', { lang, hasBundle: i18n.hasResourceBundle(lang, 'translation'), isInitialized: i18n.isInitialized });
  } catch (error) {
    logDebug('loadInitialLanguage ERROR', { lang, error: error instanceof Error ? error.message : String(error) });
    throw error;
  }
}

// Одновременные запросы одного языка делят одну загрузку. Промис нельзя
// кэшировать глобально: RU-промис не загружает EN-словарь.
const translationLoadPromises = new Map<SupportedLanguage, Promise<void>>();

function getTranslationLoadPromise(lang: SupportedLanguage): Promise<void> {
  const existingPromise = translationLoadPromises.get(lang);
  if (existingPromise) {
    return existingPromise;
  }
  const loadPromise = loadInitialLanguage(lang).then(() => {
    // Эмитируем событие загрузки для триггера ре-рендера
    i18n.emit('loaded', { lng: lang, ns: ['translation'] });
  });
  translationLoadPromises.set(lang, loadPromise);
  const clearSettledPromise = () => {
    if (translationLoadPromises.get(lang) === loadPromise) {
      translationLoadPromises.delete(lang);
    }
  };
  void loadPromise.then(clearSettledPromise, clearSettledPromise);
  return loadPromise;
}

// Инициализируем пакет для текущего языка
// Не блокируем старт приложения — загрузка идёт асинхронно
logDebug('config.ts init', { initialLang, isInitialized: i18n.isInitialized, hasBundle: i18n.hasResourceBundle(initialLang, 'translation') });
void getTranslationLoadPromise(initialLang).catch(() => {});

// Экспортируем функцию для ожидания загрузки базового translation
export function waitForTranslation(lang: SupportedLanguage = normalizeLanguage(i18n.language)): Promise<void> {
  const targetLang = lang;
  logDebug('waitForTranslation called', { targetLang, hasBundle: i18n.hasResourceBundle(targetLang, 'translation'), hasPromise: translationLoadPromises.has(targetLang) });
  if (i18n.hasResourceBundle(targetLang, 'translation')) {
    logDebug('waitForTranslation already loaded', { targetLang });
    return Promise.resolve();
  }
  return getTranslationLoadPromise(targetLang);
}

export function getCurrentLanguage(): SupportedLanguage {
  return normalizeLanguage(i18n.resolvedLanguage || i18n.language);
}

export type LanguageSwitchResult = {
  status: 'applied' | 'superseded' | 'failed';
  language: SupportedLanguage;
};

type LanguagePreflight = (language: SupportedLanguage) => Promise<void>;

const languagePreflights = new Set<LanguagePreflight>();
let languagePreflightGeneration = 0;

export function registerLanguagePreflight(preflight: LanguagePreflight): () => void {
  languagePreflights.add(preflight);
  languagePreflightGeneration += 1;
  return () => {
    if (languagePreflights.delete(preflight)) {
      languagePreflightGeneration += 1;
    }
  };
}

async function runStableLanguagePreflights(language: SupportedLanguage): Promise<void> {
  let generation: number;
  do {
    generation = languagePreflightGeneration;
    const preflights = [...languagePreflights];
    const results = await Promise.allSettled(
      preflights.map((preflight) => preflight(language)),
    );
    if (generation === languagePreflightGeneration) {
      const failure = results.find((result) => result.status === 'rejected');
      if (failure?.status === 'rejected') {
        throw failure.reason;
      }
    }
  } while (generation !== languagePreflightGeneration);
}

let latestLanguageIntent = 0;
let languageSwitchQueue: Promise<void> = Promise.resolve();

export async function switchLanguage(language: SupportedLanguage): Promise<LanguageSwitchResult> {
  const intent = ++latestLanguageIntent;
  try {
    await waitForTranslation(language);
  } catch {
    return {
      status: intent === latestLanguageIntent ? 'failed' : 'superseded',
      language,
    };
  }

  if (intent !== latestLanguageIntent) {
    return { status: 'superseded', language };
  }

  const applyLanguage = languageSwitchQueue.then(async (): Promise<LanguageSwitchResult> => {
    if (intent !== latestLanguageIntent) {
      return { status: 'superseded', language };
    }
    try {
      await runStableLanguagePreflights(language);
      if (intent !== latestLanguageIntent) {
        return { status: 'superseded', language };
      }
      await i18n.changeLanguage(language);
    } catch {
      return { status: 'failed', language };
    }
    if (intent !== latestLanguageIntent) {
      return { status: 'superseded', language };
    }
    setDocumentLanguage(language);
    // Запоминаем выбор — его читает initialLang при следующей загрузке. Без этой строки
    // ключ 'language' не писал никто, ветка чтения была мертва по построению, и оператор,
    // переключивший интерфейс на английский, терял выбор на первом же F5.
    // Пишем только у 'applied': отменённая (superseded) и упавшая смена ничего не запоминают.
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('language', language);
      }
    } catch {
      // Приватный режим или запрет на хранилище: язык применён, просто не переживёт перезагрузку.
    }
    return {
      status: 'applied',
      language,
    };
  });
  languageSwitchQueue = applyLanguage.then(() => undefined, () => undefined);
  return applyLanguage;
}

export default i18n;
